import React, { useState, useRef } from "react";
import { useLang } from "@/i18n";
import { api, BACKEND_URL } from "@/api";
import { toast } from "sonner";
import { Camera, CheckCircle2, CircleAlert, Loader2, Upload } from "lucide-react";
import { OrnateHeartDivider } from "./FloralOrnaments";

const makeUploadId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const formatBytes = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return "";
    const units = ["B", "KB", "MB", "GB"];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / (1024 ** index);
    return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
};

export default function PhotoUpload({ slug, guestName }) {
    const { t } = useLang();
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [uploadItems, setUploadItems] = useState([]);
    const [uploaded, setUploaded] = useState([]);

    const onPick = () => fileRef.current?.click();

    const updateUploadItem = (id, patch) => {
        setUploadItems((items) => items.map((item) => (
            item.id === id ? { ...item, ...patch } : item
        )));
    };

    const onFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const nextItems = files.map((file) => ({
            id: makeUploadId(),
            name: file.name,
            size: file.size,
            progress: 0,
            status: "queued",
            error: "",
        }));
        setUploadItems(nextItems);
        setUploading(true);
        const completed = [];
        let failed = false;

        try {
            for (let index = 0; index < files.length; index += 1) {
                const file = files[index];
                const item = nextItems[index];
                const form = new FormData();
                form.append("files", file);
                if (guestName) form.append("sender_name", guestName);

                try {
                    updateUploadItem(item.id, { status: "uploading", progress: 0, error: "" });
                    const resp = await api.post(`/invite/${slug}/photos`, form, {
                        headers: { "Content-Type": "multipart/form-data" },
                        onUploadProgress: (event) => {
                            if (!event.total) return;
                            const progress = Math.min(100, Math.round((event.loaded * 100) / event.total));
                            updateUploadItem(item.id, {
                                progress,
                                status: progress >= 100 ? "saving" : "uploading",
                            });
                        },
                    });
                    completed.push(...(resp.data?.uploaded || []));
                    updateUploadItem(item.id, { status: "done", progress: 100, error: "" });
                } catch (err) {
                    failed = true;
                    const message = err?.response?.data?.detail || "Upload failed";
                    updateUploadItem(item.id, { status: "failed", error: message });
                }
            }

            if (completed.length) {
                setUploaded((u) => [...u, ...completed]);
                toast.success(t("photosUploaded"));
            }
            if (failed) {
                toast.error(t("someUploadsFailed"));
            }
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const uploadStatusText = (item) => {
        if (item.status === "done") return t("uploadDone");
        if (item.status === "failed") return item.error || t("uploadFailed");
        if (item.status === "saving") return t("uploadSaving");
        if (item.status === "uploading") return t("uploading");
        return t("uploadQueued");
    };

    return (
        <section data-testid="photo-upload-section" className="py-16 px-6 wd-paper">
            <div className="max-w-xl mx-auto text-center">
                <h3 className="wd-heading text-3xl md:text-4xl text-[var(--wd-primary)] mt-2">
                    {t("sharePhotos")}
                </h3>
                <p className="mt-3 text-[var(--wd-text-muted)] text-sm">{t("uploadPhotosText")}</p>
                <div className="mt-6 flex justify-center"><OrnateHeartDivider /></div>

                <div className="mt-10">
                    <input
                        ref={fileRef}
                        type="file"
                        multiple
                        accept="image/*,video/*"
                        className="hidden"
                        onChange={onFiles}
                        data-testid="photo-input"
                    />
                    <button
                        type="button"
                        onClick={onPick}
                        disabled={uploading}
                        className="wd-btn inline-flex items-center gap-2"
                        data-testid="photo-upload-btn"
                    >
                        {uploading ? (
                            <>
                                <Upload size={16} className="animate-bounce" />
                                {t("uploading")}
                            </>
                        ) : (
                            <>
                                <Camera size={16} />
                                {t("uploadYourPhotos")}
                            </>
                        )}
                    </button>
                </div>

                {uploadItems.length > 0 && (
                    <div className="mt-6 space-y-3 text-left" data-testid="photo-upload-progress-list">
                        {uploadItems.map((item) => (
                            <div
                                key={item.id}
                                className="rounded-lg border border-[var(--wd-border)] bg-white/70 px-3 py-3 shadow-sm"
                                data-testid="photo-upload-progress-item"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 text-[var(--wd-primary)]">
                                        {item.status === "done" ? (
                                            <CheckCircle2 size={18} />
                                        ) : item.status === "failed" ? (
                                            <CircleAlert size={18} />
                                        ) : (
                                            <Loader2 size={18} className={item.status === "queued" ? "" : "animate-spin"} />
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center justify-between gap-3">
                                            <p className="truncate text-sm font-medium text-[var(--wd-text)]" title={item.name}>
                                                {item.name}
                                            </p>
                                            <span className="shrink-0 text-xs tabular-nums text-[var(--wd-text-muted)]">
                                                {item.progress}%
                                            </span>
                                        </div>
                                        <div className="mt-1 flex items-center justify-between gap-3 text-xs text-[var(--wd-text-muted)]">
                                            <span>{uploadStatusText(item)}</span>
                                            {item.size > 0 && <span>{formatBytes(item.size)}</span>}
                                        </div>
                                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--wd-border)]">
                                            <div
                                                className={`h-full rounded-full transition-all duration-300 ${
                                                    item.status === "failed" ? "bg-red-500" : "bg-[var(--wd-primary)]"
                                                }`}
                                                style={{ width: `${item.progress}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {uploaded.length > 0 && (
                    <div className="mt-10 grid grid-cols-3 gap-3" data-testid="uploaded-photo-grid">
                        {uploaded.map((p) => (
                            <div key={p.id} className="aspect-square overflow-hidden rounded-lg border border-[var(--wd-border)]">
                                {p.url && p.media_type === "video" ? (
                                    <video
                                        src={`${BACKEND_URL}${p.url}`}
                                        className="w-full h-full object-cover"
                                        controls
                                    />
                                ) : p.url ? (
                                    <img
                                        src={`${BACKEND_URL}${p.url}`}
                                        alt="Uploaded"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <a
                                        href={p.drive_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full h-full flex items-center justify-center bg-[var(--wd-paper)] text-xs text-[var(--wd-text-muted)] px-2"
                                    >
                                        View upload
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
